import React from "react";
import {
    Box, Grid, Paper, Typography, Chip, Tooltip, IconButton,
    CircularProgress, TablePagination, TextField, MenuItem, Alert,
} from "@mui/material";
import GroupWorkIcon from "@mui/icons-material/GroupWork";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import RefreshIcon from "@mui/icons-material/Refresh";
import FilterListIcon from "@mui/icons-material/FilterList";

import { SECTION_COLORS } from "../../constants";
import EmptyState from "../shared/EmptyState";
import ErrorState from "../shared/ErrorState";
import StatusChip from "../shared/StatusChip";
import ListToolbar from "../shared/ListToolbar";
import { canRecomputeDynamicSegment } from "./segmentConditionsUtils";
import { SEGMENT_SORT_OPTIONS } from "./segmentListApi";
import { resolveSegmentsUi } from "../../utils/segmentUiCatalogDefaults";

const fmtDate = (v) => v ? new Date(v).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "–";

const SegmentsTab = ({
    segmentsApi,
    onView,
    onEdit,
    onDelete,
    onViewMembers,
    onRecomputeSegment,
    usesUnomi = false,
    segmentsUi: segmentsUiProp,
}) => {
    const segmentsUi = segmentsUiProp ?? resolveSegmentsUi(null);
    const {
        listFilters = {},
        updateListFilters,
        sortBy = "name_asc",
        updateListSort,
        hasServerFilters,
        total: serverTotal = 0,
        page: serverPage = 0,
        pageSize = 10,
        loading,
        error,
        unomiSync,
        goToPage,
        refetchFromStart,
        refetch,
    } = segmentsApi;

    const { search = "", activeFilter = "all", kindFilter = "all", providerFilter = "all" } = listFilters;

    const displayRows = Array.isArray(segmentsApi.items)
        ? segmentsApi.items
        : (Array.isArray(segmentsApi.data) ? segmentsApi.data : []);

    if (error) {
        return (
            <ErrorState
                error={error}
                onRetry={() => refetchFromStart?.({ forceSync: true }) ?? refetch?.()}
            />
        );
    }

    const showEmptyCatalog = serverTotal === 0 && !loading && !hasServerFilters;
    const showEmptyFiltered = serverTotal === 0 && !loading && hasServerFilters;

    return (
        <Box>
            <ListToolbar
                search={search}
                onSearch={(v) => updateListFilters?.({ search: v })}
                activeFilter={activeFilter}
                onActiveFilter={(v) => updateListFilters?.({ activeFilter: v })}
                searchPlaceholder="Rechercher par nom ou description…"
                sortValue={sortBy}
                onSortChange={(v) => updateListSort?.(v)}
                sortOptions={SEGMENT_SORT_OPTIONS}
            >
                <TextField
                    select
                    size="small"
                    label="Type"
                    value={kindFilter}
                    onChange={(e) => updateListFilters?.({ kindFilter: e.target.value })}
                    sx={{ minWidth: 150, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                >
                    <MenuItem value="all">Tous</MenuItem>
                    <MenuItem value="dynamic">Dynamiques</MenuItem>
                    <MenuItem value="static">Statiques</MenuItem>
                </TextField>
                <TextField
                    select
                    size="small"
                    label={segmentsUi.list.providerFilterLabel}
                    value={providerFilter}
                    onChange={(e) => updateListFilters?.({ providerFilter: e.target.value })}
                    sx={{ minWidth: 200, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                >
                    <MenuItem value="all">Toutes origines</MenuItem>
                    <MenuItem value="INTERNAL">{segmentsUi.badges.providerInternal}</MenuItem>
                    <MenuItem value="UNOMI">{segmentsUi.badges.providerUnomi}</MenuItem>
                </TextField>
            </ListToolbar>

            {usesUnomi && unomiSync === "failed" && (
                <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                    {segmentsUi.list.unomiSyncFailedHint}
                </Alert>
            )}

            {hasServerFilters && (
                <Alert severity="info" icon={<FilterListIcon />} sx={{ mb: 2, borderRadius: 2, py: 0.5 }}>
                    Filtres actifs côté serveur — {serverTotal} segment{serverTotal !== 1 ? "s" : ""} au total
                    {loading ? " (chargement…)" : ""}.
                </Alert>
            )}

            {loading && displayRows.length === 0 && (
                <Box py={3} display="flex" justifyContent="center">
                    <CircularProgress size={28} />
                </Box>
            )}

            {showEmptyCatalog && (
                <EmptyState message="Aucun segment configuré" color={SECTION_COLORS.segments.main} />
            )}

            {showEmptyFiltered && (
                <EmptyState
                    message="Aucun segment ne correspond à ces filtres"
                    color={SECTION_COLORS.segments.main}
                />
            )}

            {!showEmptyCatalog && !showEmptyFiltered && (
                <>
                    <Grid container spacing={2}>
                        {displayRows.map((segment) => {
                            const hasConditions = segment.is_dynamic && segment.conditions
                                && Object.keys(segment.conditions).length > 0;
                            return (
                                <Grid item xs={12} sm={6} md={4} key={segment.id}>
                                    <Paper elevation={0} sx={{
                                        p: 2.5, borderRadius: 3, position: "relative", overflow: "hidden",
                                        border: `1px solid ${SECTION_COLORS.segments.main}15`,
                                        background: `linear-gradient(135deg, ${SECTION_COLORS.segments.main}06 0%, transparent 100%)`,
                                        boxShadow: "0 8px 24px rgba(15, 23, 42, 0.10), 0 2px 8px rgba(15, 23, 42, 0.06)",
                                        transition: "all 0.25s ease",
                                        "&:hover": {
                                            transform: "translateY(-2px)",
                                            boxShadow: "0 14px 30px rgba(15, 23, 42, 0.14), 0 6px 14px rgba(15, 23, 42, 0.10)",
                                            borderColor: `${SECTION_COLORS.segments.main}30`,
                                        },
                                    }}>
                                        <Box position="absolute" top={10} right={10} display="flex" flexDirection="column" gap={0.5} alignItems="flex-end">
                                            <Tooltip title={segment.is_dynamic ? "Segment dynamique" : "Segment statique"} placement="left">
                                                <Chip
                                                    label={segment.is_dynamic ? "Dynamique" : "Statique"}
                                                    size="small"
                                                    sx={{
                                                        fontWeight: 700, fontSize: 10,
                                                        bgcolor: segment.is_dynamic ? "#f0f9ff" : "#f8fafc",
                                                        color: segment.is_dynamic ? "#0369a1" : "#475569",
                                                    }}
                                                />
                                            </Tooltip>
                                            {(segment.provider === "UNOMI" || usesUnomi) && (
                                                <Chip label="Unomi" size="small"
                                                    sx={{ fontWeight: 700, fontSize: 10, bgcolor: "#fdf4ff", color: "#a21caf" }} />
                                            )}
                                        </Box>

                                        <Box display="flex" alignItems="center" gap={1} mb={0.5} mr={12}>
                                            <GroupWorkIcon sx={{ color: SECTION_COLORS.segments.main, fontSize: 20, flexShrink: 0 }} />
                                            <Typography fontWeight={700} fontSize={14} fontFamily="Montserrat, sans-serif" color="#1a1a2e" noWrap>
                                                {segment.name}
                                            </Typography>
                                        </Box>

                                        <Typography fontSize={11} color="text.secondary" noWrap mb={1.5} sx={{ pl: 3.5 }}>
                                            {segment.description || "Aucune description"}
                                        </Typography>

                                        <Box display="flex" gap={1} mb={1.5} flexWrap="wrap">
                                            {hasConditions && (
                                                <Chip
                                                    label="Conditions"
                                                    size="small"
                                                    sx={{ fontSize: 10, fontWeight: 600, bgcolor: "#fffbeb", color: "#92400e" }}
                                                />
                                            )}
                                            {typeof segment.member_count === "number" && (
                                                <Chip
                                                    label={`${segment.member_count} membre(s)`}
                                                    size="small"
                                                    sx={{ fontSize: 10, fontWeight: 600, bgcolor: "#ecfdf5", color: "#047857" }}
                                                />
                                            )}
                                            {segment.is_dynamic && (
                                                <Chip
                                                    label={segment.needs_recompute ? "Recalcul requis" : `Calculé: ${fmtDate(segment.last_computed_at)}`}
                                                    size="small"
                                                    sx={{
                                                        fontSize: 10, fontWeight: 600,
                                                        bgcolor: segment.needs_recompute ? "#fff7ed" : "#fafbfd",
                                                        color: segment.needs_recompute ? "#c2410c" : "#64748b",
                                                    }}
                                                />
                                            )}
                                            {segment.conditions_format === "unomi_only" && (
                                                <Chip label={segmentsUi.badges.unomiOnly} size="small"
                                                    sx={{ fontSize: 10, fontWeight: 600, bgcolor: "#fff7ed", color: "#c2410c" }} />
                                            )}
                                            {segment.can_delete === false && (
                                                <Chip label={segmentsUi.badges.referenced} size="small"
                                                    sx={{ fontSize: 10, fontWeight: 600, bgcolor: "#fef2f2", color: "#b91c1c" }} />
                                            )}
                                        </Box>

                                        <Box display="flex" alignItems="center" justifyContent="space-between">
                                            <StatusChip active={segment.active} />
                                            <Box display="flex" gap={0.5}>
                                                {onViewMembers && (
                                                    <Tooltip title={segment.is_dynamic ? "Voir les membres" : "Gérer les membres"}>
                                                        <IconButton size="small" onClick={() => onViewMembers(segment)}
                                                            sx={{
                                                                color: SECTION_COLORS.segments.main,
                                                                bgcolor: `${SECTION_COLORS.segments.main}12`,
                                                                "&:hover": { bgcolor: `${SECTION_COLORS.segments.main}22` },
                                                                width: 30, height: 30,
                                                            }}>
                                                            <PeopleAltIcon sx={{ fontSize: 16 }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {segment.is_dynamic && onRecomputeSegment && canRecomputeDynamicSegment(segment) && (
                                                    <Tooltip title={segmentsUi.recompute.tooltip}>
                                                        <IconButton size="small" onClick={() => onRecomputeSegment(segment)}
                                                            sx={{
                                                                color: "#0369a1",
                                                                bgcolor: "#f0f9ff",
                                                                "&:hover": { bgcolor: "#e0f2fe" },
                                                                width: 30, height: 30,
                                                            }}>
                                                            <RefreshIcon sx={{ fontSize: 16 }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                <Tooltip title="Voir le détail">
                                                    <IconButton size="small" onClick={() => onView(segment)}
                                                        sx={{
                                                            color: SECTION_COLORS.segments.main,
                                                            bgcolor: `${SECTION_COLORS.segments.main}10`,
                                                            "&:hover": { bgcolor: `${SECTION_COLORS.segments.main}20` }, width: 30, height: 30,
                                                        }}>
                                                        <VisibilityIcon sx={{ fontSize: 16 }} />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Modifier">
                                                    <IconButton size="small" onClick={() => onEdit(segment)}
                                                        sx={{ color: "#6366f1", bgcolor: "#eef2ff", "&:hover": { bgcolor: "#e0e7ff" }, width: 30, height: 30 }}>
                                                        <EditIcon sx={{ fontSize: 16 }} />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Supprimer">
                                                    <IconButton size="small" onClick={() => onDelete(segment)}
                                                        sx={{ color: "#DC2626", bgcolor: "#fef2f2", "&:hover": { bgcolor: "#fee2e2" }, width: 30, height: 30 }}>
                                                        <DeleteIcon sx={{ fontSize: 16 }} />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </Box>
                                    </Paper>
                                </Grid>
                            );
                        })}
                    </Grid>

                    {serverTotal > pageSize && (
                        <TablePagination
                            component="div"
                            count={serverTotal}
                            page={serverPage}
                            onPageChange={(_, newPage) => goToPage?.(newPage)}
                            rowsPerPage={pageSize}
                            rowsPerPageOptions={[pageSize]}
                            labelDisplayedRows={({ from, to, count }) => `${from}–${to} sur ${count}`}
                            sx={{ mt: 1, borderTop: "1px solid #f0f0f0" }}
                        />
                    )}
                </>
            )}
        </Box>
    );
};

export default SegmentsTab;
